
import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  BarChart2, 
  Download, 
  Filter, 
  TrendingUp 
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  Legend
} from "recharts";

// Sample KPI data
const sampleKPIData = [
  {
    id: "1",
    employeeId: "1",
    employeeName: "Ahmad Bin Ali",
    targetSales: 15000,
    actualSales: 15240,
    kpiScore: 101.6,
    upsellingRatio: 0.25,
    totalOrders: 142,
    avgOrderValue: 107.32,
    customerRating: 4.8,
    incentiveEligible: true,
    bonusEarned: 350
  },
  {
    id: "2",
    employeeId: "2",
    employeeName: "Siti Aminah",
    targetSales: 10000,
    actualSales: 8760,
    kpiScore: 87.6,
    upsellingRatio: 0.15,
    totalOrders: 98,
    avgOrderValue: 89.38,
    customerRating: 4.5,
    incentiveEligible: false,
    bonusEarned: 0
  },
  {
    id: "3",
    employeeId: "3",
    employeeName: "Rajesh Kumar",
    targetSales: 8000,
    actualSales: 5420,
    kpiScore: 67.8,
    upsellingRatio: 0.1,
    totalOrders: 56,
    avgOrderValue: 96.79,
    customerRating: 4.0,
    incentiveEligible: false,
    bonusEarned: 0
  }
];

// Function to get color based on KPI score
const getKpiColor = (score: number) => {
  if (score >= 100) return "bg-green-600";
  if (score >= 80) return "bg-blue-600";
  if (score >= 60) return "bg-yellow-600";
  return "bg-red-600";
};

// Function to get incentive eligibility text and color
const getIncentiveDisplay = (isEligible: boolean) => {
  return {
    text: isEligible ? "Yes" : "No",
    color: isEligible ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
  };
};

const PerformanceKPI = () => {
  const [kpiData, setKpiData] = useState(sampleKPIData);
  const [showPerformanceDetails, setShowPerformanceDetails] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("kpiScore");
  
  // Sort KPI data based on selected sorting criteria
  const sortedKpiData = [...kpiData].sort((a, b) => {
    if (sortBy === "kpiScore") return b.kpiScore - a.kpiScore;
    if (sortBy === "actualSales") return b.actualSales - a.actualSales;
    if (sortBy === "customerRating") return b.customerRating - a.customerRating;
    return 0;
  });
  
  // Get selected employee data for details view
  const selectedEmployeeData = selectedEmployee 
    ? kpiData.find(employee => employee.employeeId === selectedEmployee) 
    : null;
  
  // Prepare chart data for sales comparison
  const salesComparisonData = kpiData.map(employee => ({
    name: employee.employeeName,
    target: employee.targetSales,
    actual: employee.actualSales
  }));
  
  const handleViewDetails = (employeeId: string) => {
    setSelectedEmployee(employeeId);
    setShowPerformanceDetails(true);
  };
  
  const handleExportReport = () => {
    // In a real app, this would generate and download a report
    alert("Exporting KPI report... This feature would be implemented with a proper export library");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-semibold">Performance & KPI Tracking</h2>
        <div className="flex space-x-2">
          <Select
            value={sortBy}
            onValueChange={setSortBy}
          >
            <SelectTrigger className="w-[180px]">
              <Filter className="mr-2 h-4 w-4" />
              <span>Sort by</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="kpiScore">KPI Score</SelectItem>
              <SelectItem value="actualSales">Actual Sales</SelectItem>
              <SelectItem value="customerRating">Customer Rating</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={handleExportReport}>
            <Download className="mr-2 h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableCaption>Employee performance metrics and KPI scores</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Target Sales</TableHead>
              <TableHead>Actual Sales</TableHead>
              <TableHead>KPI Score</TableHead>
              <TableHead>Customer Rating</TableHead>
              <TableHead>Incentive Eligible</TableHead>
              <TableHead>Bonus</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedKpiData.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell className="font-medium">{employee.employeeName}</TableCell>
                <TableCell>${employee.targetSales.toLocaleString()}</TableCell>
                <TableCell>${employee.actualSales.toLocaleString()}</TableCell>
                <TableCell>
                  <div className="w-full space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>{employee.kpiScore}%</span>
                    </div>
                    <Progress 
                      value={employee.kpiScore} 
                      max={120}
                      className={getKpiColor(employee.kpiScore)}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <span className="text-amber-500 mr-1">★</span> 
                    {employee.customerRating.toFixed(1)}
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    getIncentiveDisplay(employee.incentiveEligible).color
                  }`}>
                    {getIncentiveDisplay(employee.incentiveEligible).text}
                  </span>
                </TableCell>
                <TableCell>${employee.bonusEarned}</TableCell>
                <TableCell className="text-right">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleViewDetails(employee.employeeId)}
                  >
                    <BarChart2 className="mr-2 h-4 w-4" />
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-md border p-4">
          <h3 className="text-lg font-medium mb-4">Sales Achievement</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={salesComparisonData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="target" name="Target Sales" fill="#94a3b8" />
              <Bar dataKey="actual" name="Actual Sales" fill="#3b82f6">
                {salesComparisonData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.actual >= entry.target ? '#22c55e' : '#ef4444'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="rounded-md border p-4">
          <h3 className="text-lg font-medium mb-4">Top Performers (KPI)</h3>
          <div className="space-y-4">
            {sortedKpiData.slice(0, 3).map((employee, index) => (
              <div key={employee.id} className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                  index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : 'bg-amber-800'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <span className="font-medium">{employee.employeeName}</span>
                    <span className="font-bold">{employee.kpiScore}%</span>
                  </div>
                  <Progress 
                    value={employee.kpiScore} 
                    max={120}
                    className={getKpiColor(employee.kpiScore)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <div className="flex justify-between items-center text-sm text-muted-foreground mb-2">
              <span>Employee</span>
              <span>Avg. Order Value</span>
            </div>
            {sortedKpiData
              .sort((a, b) => b.avgOrderValue - a.avgOrderValue)
              .slice(0, 3)
              .map(employee => (
                <div key={`avg-${employee.id}`} className="flex justify-between items-center py-1 border-b last:border-0">
                  <span>{employee.employeeName}</span>
                  <span className="font-medium">${employee.avgOrderValue.toFixed(2)}</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>
      
      {/* Performance Details Dialog */}
      <Dialog open={showPerformanceDetails} onOpenChange={setShowPerformanceDetails}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Performance Details</DialogTitle>
          </DialogHeader>
          
          {selectedEmployeeData && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{selectedEmployeeData.employeeName}</h3>
                <div className="flex items-center space-x-1">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-lg">{selectedEmployeeData.kpiScore}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Target Sales</p>
                  <p className="text-lg font-medium">${selectedEmployeeData.targetSales.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Actual Sales</p>
                  <p className="text-lg font-medium">${selectedEmployeeData.actualSales.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Upselling Ratio</p>
                  <p className="text-lg font-medium">{(selectedEmployeeData.upsellingRatio * 100).toFixed(0)}%</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-lg font-medium">{selectedEmployeeData.totalOrders}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Avg. Order Value</p>
                  <p className="text-lg font-medium">${selectedEmployeeData.avgOrderValue.toFixed(2)}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Customer Rating</p>
                  <p className="text-lg font-medium flex items-center">
                    <span className="text-amber-500 mr-1">★</span> 
                    {selectedEmployeeData.customerRating.toFixed(1)}/5.0
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Incentive & Bonus</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Incentive Eligible</p>
                    <p className="text-lg font-medium">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        getIncentiveDisplay(selectedEmployeeData.incentiveEligible).color
                      }`}>
                        {getIncentiveDisplay(selectedEmployeeData.incentiveEligible).text}
                      </span>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Bonus Earned</p>
                    <p className="text-lg font-medium">${selectedEmployeeData.bonusEarned}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <Button variant="outline" className="w-full">View Full Performance History</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PerformanceKPI;
